/* eslint-disable @typescript-eslint/no-explicit-any */
import { Award, BookMarked, CircleX, CopyX, Flag, Goal, ListX, LogOut, Monitor, MoveRight, NotebookText, ScanSearch, TableProperties } from "lucide-react";
import { useEffect, useState } from "react";
import Spreadsheet from "react-spreadsheet";
import * as XLSX from "xlsx";
import Draggable from "react-draggable";
import ReactQuill from "react-quill";
import EndingScreen from "./EndingScreen";
import { IFRSDTO } from "../../api/Quiz/GetIFRSById";

type OpenWindow = {
    id: string;
    filePath: string;
    type: "pdf" | "excel" | "requirements" | "spreadsheet" | "wordProcessor";
    title: string;
    data?: any[];
};

// type Exhibit = {
//     id: string;
//     title: string;
//     type: "pdf" | "excel";
//     filePath: string;
// };
// type Question = {
//     id: string;
//     text: string;
//     exhibits: Exhibit[];
//     requirements: string;
//     responseOptions: {
//         wordProcessor?: boolean;
//         spreadsheet?: boolean;
//         spreadsheetFilePath?: string; // Path  to the spreadsheet file for this question
//     };
// };

const ExamContent = ({data}: {data: IFRSDTO}) => {
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0); // Track current question
    const [isLastQuestion, setIsLastQuestion] = useState(false); // Track if it's the last question
    const [responseExcelData, setResponseExcelData] = useState<any[]>([]); // Editable Excel data for Response Options
    const [wordProcessorData, setWordProcessorData] = useState(""); // Editable data for Word Processor
    const [openWindows, setOpenWindows] = useState<OpenWindow[]>([]); // Track all open windows
    const [initialQuestionData, setInitialQuestionData] = useState<any[]>([]); // To store initial data
    const [isExamEnded, setIsExamEnded] = useState(false); // Track if the exam has ended
    const questions = data?.ifrsQuestions || [];
    // const questions: Question[] = [
    //     {
    //         id: "q1",
    //         text: "You are the financial controller of Omega, a listed entity with a number of subsidiaries. Omega prepares financial statements to 30 September each year. One of Omega’s trainee accountants is responsible for preparing the first draft of the consolidated financial statement for the year ended 30 September 20X5. The trainee accountant has raised a series of questions relating to those financial statements.",
    //         exhibits: [
    //             { id: "1", title: "Segment Disclosure", type: "pdf", filePath: "/example.pdf" },
    //             { id: "2", title: "Financial Spreadsheet", type: "excel", filePath: "/exam_data.xlsx" },
    //         ],
    //         requirements: "Discuss the financial implications of the consolidated statements and provide adjustments for trainee errors.",
    //         responseOptions: {
    //             wordProcessor: true,
    //             spreadsheet: true,
    //             spreadsheetFilePath: "/exam_answers.xlsx",
    //         },
    //     },
    //     {
    //         id: "q2",
    //         text: "You are tasked with reviewing the financial consolidation for Beta, a subsidiary of Omega. Beta has recently acquired assets that require different reporting methods. Please address the trainee accountant's questions about this consolidation.",
    //         exhibits: [
    //             { id: "3", title: "Consolidation Guidelines", type: "pdf", filePath: "/guidelines.pdf" },
    //             { id: "4", title: "Asset Acquisition Spreadsheet", type: "excel", filePath: "/assets.xlsx" },
    //         ],
    //         requirements: "Analyze the asset acquisition data and suggest a suitable reporting method.",
    //         responseOptions: {
    //             wordProcessor: true,
    //             spreadsheet: false,
    //         },
    //     },
    // ];
    const [questionData, setQuestionData] = useState<
        { id: string; spreadsheetData: any[]; wordProcessorContent: string }[]
    >(questions.map((question) => ({
        id: question.id,
        spreadsheetData: [], // Change this to any[] instead of never[]
        wordProcessorContent: "",
    })));
    const [flaggedQuestions, setFlaggedQuestions] = useState<boolean[]>(Array(questions.length).fill(false)); // Track flagged questions
    const handleFlagQuestion = (index: number) => {
        setFlaggedQuestions((prev) => {
            const newFlags = [...prev];
            newFlags[index] = !newFlags[index]; // Toggle the flagged state
            return newFlags;
        });
    };

    const modules = {
        toolbar: [
            [{ 'font': [] }],
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'script': 'sub' }, { 'script': 'super' }],
            [{ 'indent': '-1' }, { 'indent': '+1' }],
            [{ 'direction': 'rtl' }],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'align': [] }],
            ['clean']
        ]
    };

    const fetchExcelData = async (filePath: string): Promise<any[]> => {
        try {
            const response = await fetch(filePath);
            const arrayBuffer = await response.arrayBuffer();
            const workbook = XLSX.read(arrayBuffer, { type: "array" });
            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];
            const jsonData: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });
            return jsonData.map((row) => row.map((cell) => ({ value: cell || "" })));
        } catch {
            // Fallback to an empty spreadsheet
            return Array(10)
                .fill(0)
                .map(() => Array(5).fill({ value: "" }));
        }
    };
    useEffect(() => {
        // Fetch data for all questions on component mount
        const fetchAllData = async () => {
            const initialData = await Promise.all(
                questions.map(async (question) => {
                    let spreadsheetData = [];
                    if (question.responseOptions.spreadsheetFilePath) {
                        spreadsheetData = await fetchExcelData(question.responseOptions.spreadsheetFilePath);
                    } else {
                        // Default empty data
                        spreadsheetData = Array(10).fill(0).map(() => Array(5).fill({ value: "" }));
                    }

                    return {
                        id: question.id,
                        spreadsheetData,
                        wordProcessorContent: "", // Initialize as empty
                    };
                })
            );

            setQuestionData(initialData);
            setInitialQuestionData(initialData); // Store initial data for comparison
            // Set data for the first question
            const firstQuestion = initialData[0];
            setResponseExcelData(firstQuestion.spreadsheetData);
            setWordProcessorData(firstQuestion.wordProcessorContent);
        };

        fetchAllData();
    }, []); // Runs only once on component mount

    useEffect(() => {
        // Load data for the current question from saved questionData
        const currentQuestionData = questionData[currentQuestionIndex];
        if (currentQuestionData) {
            setResponseExcelData(currentQuestionData.spreadsheetData);
            setWordProcessorData(currentQuestionData.wordProcessorContent || "");
        }
    }, [currentQuestionIndex, questionData]);

    const handleSpreadsheetChange = (data: any[]) => {
        setResponseExcelData(data);

        // Save changes to questionData for the current question
        setQuestionData((prev) =>
            prev.map((question, index) =>
                index === currentQuestionIndex
                    ? { ...question, spreadsheetData: data }
                    : question
            )
        );
    };

    const handleWordProcessorChange = (content: string) => {
        setWordProcessorData(content);
        // Update questionData for the current question
        setQuestionData((prev) =>
            prev.map((question, index) =>
                index === currentQuestionIndex
                    ? { ...question, wordProcessorContent: content }
                    : question
            )
        );
    };

    const handleWindowOpen = async (type: OpenWindow["type"], title: string, filePath?: string) => {
        // Check if a window with the same title and type is already open
        const existingWindow = openWindows.find(
            (win) => win.title === title && win.type === type
        );
        if (existingWindow) return; // Prevent duplicate windows

        let windowData: any[] | undefined;

        if (type === "spreadsheet") {
            const question = questionData[currentQuestionIndex];
            if (question.spreadsheetData.length === 0 && filePath) {
                windowData = await fetchExcelData(filePath);
            } else {
                windowData = question.spreadsheetData;
            }
        }

        if (type === "excel") {
            // Fetch the exhibit spreadsheet data
            if (filePath) {
                windowData = await fetchExcelData(filePath);
            } else {
                windowData = Array(10).fill(0).map(() => Array(5).fill({ value: "" })); // Default empty data
            }
        }

        if (type === "wordProcessor") {
            setWordProcessorData(questionData[currentQuestionIndex].wordProcessorContent);
        }

        const newWindow: OpenWindow = {
            id: `${type}-${Date.now()}`, // Unique ID
            type,
            title,
            filePath: filePath || "",
            data: windowData,
        };

        setOpenWindows((prev) => [...prev, newWindow]);
    };

    const handleNextQuestion = () => {
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex((prevIndex) => prevIndex + 1);
            saveCurrentQuestionData(); // Save changes before navigating
            setIsLastQuestion(false);
        } else {
            setIsLastQuestion(true); // Set to indicate it's the last question
        }
    };

    const handlePreviousQuestion = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex((prevIndex) => prevIndex - 1);
            saveCurrentQuestionData(); // Save changes before navigating
            setIsLastQuestion(false);
        }
    };

    const saveCurrentQuestionData = () => {
        setQuestionData((prev) =>
            prev.map((question, index) =>
                index === currentQuestionIndex
                    ? { ...question, spreadsheetData: responseExcelData }
                    : question
            )
        );
    };

    const currentQuestion = questions[currentQuestionIndex];

    const handleCloseWindow = (id: string) => {
        const window = openWindows.find((win) => win.id === id);

        if (window) {
            if (window.type === "spreadsheet") {
                setQuestionData((prev) =>
                    prev.map((question, index) =>
                        index === currentQuestionIndex
                            ? { ...question, spreadsheetData: responseExcelData }
                            : question
                    )
                );
            } else if (window.type === "wordProcessor") {
                setQuestionData((prev) =>
                    prev.map((question, index) =>
                        index === currentQuestionIndex
                            ? { ...question, wordProcessorContent: wordProcessorData }
                            : question
                    )
                );
            }
        }

        setOpenWindows((prev) => prev.filter((win) => win.id !== id));
    };


    const renderWindowContent = (window: OpenWindow) => {
        switch (window.type) {
            case "pdf":
                return <iframe src={window.filePath} className="w-full h-full" title={window.title} />;
            case "excel":
                return (
                    <div className="h-[calc(100%-62px)] w-full p-5 overflow-scroll">
                        {window.data && window.data.length > 0 ? (
                            <table className="table-auto border-collapse border border-gray-400 w-full text-left">
                                <thead>
                                    <tr>
                                    {window.data[0].map((_cell: any, index: number) => ( // Explicitly type index as number
                                            <th
                                                key={index}
                                                className="border border-gray-300 px-2 py-1 font-bold bg-gray-200 min-w-[200px]"
                                            >
                                                Column {index + 1}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {window.data.map((row, rowIndex) => (
                                        <tr key={rowIndex} className="border-b">
                                            {row.map((cell: { value: string }, cellIndex: number) => (
                                                <td
                                                    key={cellIndex}
                                                    className="border border-gray-300 px-2 py-1 min-w-[200px]"
                                                >
                                                    {cell.value}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p className="text-gray-500">Loading Excel data...</p>
                        )}
                    </div>
                );
            case "requirements":
                return <div className="p-5">{currentQuestion.requirements}</div>;
            case "spreadsheet":
                return (
                    <div className="h-[calc(100%-62px)] p-5 overflow-y-scroll">
                        {responseExcelData.length > 0 ? (
                            <Spreadsheet data={responseExcelData}
                                onChange={handleSpreadsheetChange}
                            />
                        ) : (
                            <p className="text-gray-500">Loading data...</p>
                        )}
                    </div>
                );
            case "wordProcessor":
                return (
                    <div className="p-5 h-[95%]">
                        <ReactQuill
                            modules={modules}
                            theme="snow"
                            value={wordProcessorData}
                            onChange={handleWordProcessorChange}
                            placeholder="Write your response here..."
                            className="h-full"
                        />
                    </div>
                );
            default:
                return null;
        }
    };

    useEffect(() => {
        const loadResponseExcel = async () => {
            const data = await fetchExcelData("/exam_answers.xlsx"); // Replace with your file path
            setResponseExcelData(data);
        };

        loadResponseExcel();
    }, []);

    return (
        <div className="h-screen justify-between flex flex-col">
            {isExamEnded ? (
                <EndingScreen/>
            ) : (
                <>
                    {isLastQuestion ? (
                        <>
                            <div>
                                <div className="py-4 bg-[#DCDDDD] px-3 flex items-center justify-between">
                                    <h1 className="font-bold text-sm">
                                        Diploma in International Financial Reporting (IFR - INT) Sample Questions - June 2024
                                    </h1>
                                </div>
                                <div className="bg-[#F2F2F2] py-2 font-bold text-sm px-3 text-center" >
                                    Item Review Screen
                                </div>
                                <div className="py-3 bg-[#DCDDDD] px-3 flex items-center justify-between">
                                    <h1 className="font-bold text-sm">
                                        Exam Section
                                    </h1>
                                </div>

                            </div>
                            <div className="max-h-[calc(100vh_-_132px)] h-full flex gap-3 bg-white">
                                <div className="mt-5 border-t w-full">
                                    {questions.map((question, index) => { // Map over questions
                                        // Determine if the question is attempted
                                        const isAttempted = JSON.stringify(questionData[index].spreadsheetData) !== JSON.stringify(initialQuestionData[index].spreadsheetData) ||
                                            questionData[index].wordProcessorContent !== initialQuestionData[index].wordProcessorContent;

                                        // Determine the class and text based on attempted status
                                        const statusText = isAttempted ? "Attempted" : "Not Attempted";
                                        const statusClass = isAttempted ? "text-green-500" : "text-red-600";

                                        return (
                                            <div className="flex gap-80 border-b w-full py-1.5" key={question.id}>
                                                <div className="flex">
                                                    <div className="px-2">
                                                        <Flag className={flaggedQuestions[index] ? "text-secondary fill-secondary" : ""} size={20} />
                                                    </div>
                                                    <span onClick={() => {
                                                        setCurrentQuestionIndex(index); // Set current question index on click
                                                        setIsLastQuestion(false); // Set isLastQuestion to false
                                                    }} className="text-sm font-semibold cursor-pointer w-72 hover:text-secondary duration-300 transition-all">Question {index + 1}</span>
                                                </div>
                                                <span className={`text-sm font-semibold ${statusClass}`}> {/* Use the stored class */}
                                                    {statusText} {/* Use the stored text */}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="flex justify-between bg-[#DCDDDD] px-3">
                                <button
                                    onClick={() => {
                                        setIsExamEnded(true); // Set to show alternative content when ending the exam
                                    }}
                                    className="font-bold text-sm flex items-center gap-1 py-2 pr-2 border-r border-white hover:text-secondary duration-300 transition-all"
                                >
                                    <LogOut size={16} /> End Exam
                                </button>

                                <div className="flex justify-end items-center gap-3">
                                    <button
                                        onClick={() => {
                                            setCurrentQuestionIndex(0);
                                            setIsLastQuestion(false); // Set isLastQuestion to false
                                        }}
                                        className="font-bold text-sm flex items-center gap-1 border-l border-white h-full p-2 hover:text-secondary duration-300 transition-all"
                                    >
                                        <ScanSearch size={16} /> Review All
                                    </button>
                                    <button
                                        onClick={() => {
                                            setCurrentQuestionIndex(0);
                                            setIsLastQuestion(false); // Set isLastQuestion to false
                                        }}
                                        className="font-bold text-sm flex items-center gap-1 border-l border-white h-full p-2 hover:text-secondary duration-300 transition-all"
                                    >
                                        <ListX size={20} /> Review Not Attempted
                                    </button>
                                    <button
                                        onClick={() => {
                                            setCurrentQuestionIndex(0);
                                            setIsLastQuestion(false); // Set isLastQuestion to false
                                        }}

                                        className="font-bold text-sm flex items-center gap-1 border-l border-white h-full p-2 hover:text-secondary duration-300 transition-all"
                                    >
                                        <Goal size={20} /> Review Flagged
                                    </button>

                                </div>

                            </div>
                        </>
                    ) : (

                        <>
                            {openWindows.map((window) => (
                                <Draggable key={window.id}>
                                    <div className="absolute top-10 left-10 z-50 bg-white shadow-lg border border-gray-300 rounded-md w-[1200px] h-[800px]">
                                        <div className="flex justify-between items-center p-2 border-b border-gray-300 cursor-move">
                                            <h2 className="font-bold text-sm">{window.title}</h2>
                                            <button
                                                className="text-gray-600 font-bold"
                                                onClick={() => handleCloseWindow(window.id)}
                                            >
                                                <CircleX />
                                            </button>
                                        </div>
                                        <div className="w-full h-[calc(100%-50px)]">{renderWindowContent(window)}</div>
                                    </div>
                                </Draggable>
                            ))}

                            <div>
                                <div className="py-4 bg-[#DCDDDD] px-3 flex items-center justify-between">
                                    <h1 className="font-bold text-sm">
                                        Diploma in International Financial Reporting (IFR - INT) Sample Questions - June 2024
                                    </h1>
                                    <div className="flex gap-2 items-center">
                                        <Monitor size={16} />
                                        <h1 className="font-bold text-sm">
                                            {currentQuestionIndex + 1} of {questions.length}
                                        </h1>

                                    </div>
                                </div>
                                <div className="bg-[#F2F2F2] h-8 text-sm flex items-center justify-between px-3" >
                                    <div></div>
                                    <div className="flex gap-3">
                                        <button
                                            onClick={() => {
                                                setOpenWindows([]);
                                            }}
                                            className="font-bold text-sm flex items-center gap-1 hover:text-secondary duration-300 transition-all"
                                        >
                                            <CopyX size={20} /> Close All
                                        </button>
                                        <button
                                            onClick={() => handleFlagQuestion(currentQuestionIndex)} // Add this line
                                            className="font-bold text-sm flex items-center gap-1 hover:text-secondary duration-300 transition-all"
                                        >
                                            <Flag className={flaggedQuestions[currentQuestionIndex] ? "text-secondary fill-secondary" : ""} size={20} /> Flag For Review
                                        </button>

                                    </div>
                                </div>
                            </div>
                            <div className="max-h-[calc(100vh_-_132px)] h-full flex gap-3 bg-[#DCDDDD]">
                                <div className="bg-white min-w-[370px] p-5 space-y-5">
                                    <div>
                                        <h1 className="font-bold">Exhibits</h1>
                                        <div className="space-y-1 mt-2">
                                            {currentQuestion?.exhibits?.map((exhibit) => (
                                                <button
                                                    key={exhibit.id }
                                                    className="px-4 py-2 border bg-[#F2F2F2] border-[#DCDDDD] w-full hover:text-secondary flex items-center gap-1 font-bold duration-300 transition-all"
                                                    onClick={() =>
                                                        handleWindowOpen(
                                                            exhibit.type,
                                                            exhibit.title,
                                                            exhibit.filePath
                                                        )
                                                    }
                                                >
                                                    <BookMarked /> {exhibit.title}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <h1 className="font-bold">Requirements</h1>
                                        <div className="space-y-1 mt-2">
                                            <button
                                                className="px-4 py-2 border bg-[#F2F2F2] border-[#DCDDDD] w-full hover:text-secondary flex items-center gap-1 font-bold duration-300 transition-all"
                                                onClick={() => handleWindowOpen("requirements", "Requirements")}
                                            >
                                                <Award /> Requirements
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <h1 className="font-bold">Response Options</h1>
                                        <div className="space-y-1 mt-2">
                                            {currentQuestion.responseOptions.spreadsheet && (
                                                <button
                                                    className="px-4 py-2 border bg-[#F2F2F2] border-[#DCDDDD] w-full hover:text-secondary flex items-center gap-1 font-bold duration-300 transition-all"
                                                    onClick={() => handleWindowOpen("spreadsheet", "Spreadsheet")}
                                                >
                                                    <TableProperties /> Spreadsheet
                                                </button>
                                            )}
                                            {currentQuestion.responseOptions.wordProcessor && (
                                                <button
                                                    className="px-4 py-2 border bg-[#F2F2F2] border-[#DCDDDD] w-full hover:text-secondary flex items-center gap-1 font-bold duration-300 transition-all"
                                                    onClick={() => handleWindowOpen("wordProcessor", "Word Processor")}
                                                >
                                                    <NotebookText /> Word Processor
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                </div>
                                <div className="bg-white w-full p-5 overflow-y-scroll">
                                    <p>{currentQuestion.text}</p>
                                </div>
                            </div>

                            <div className="py-3 bg-[#DCDDDD] px-3 flex justify-end items-center gap-3">
                                <button
                                    onClick={handlePreviousQuestion}
                                    disabled={currentQuestionIndex <= 0}
                                    className="font-bold text-sm flex items-center gap-1"
                                >
                                    <MoveRight className="rotate-180" /> Previous
                                </button>
                                <button
                                    onClick={handleNextQuestion}
                                    className="font-bold text-sm flex items-center gap-1"
                                >
                                    Next <MoveRight />
                                </button>
                            </div>
                        </>
                    )}
                </>
            )}
        </div>

    );
};

export default ExamContent;
